import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Mail, Menu, AlertCircle, MessageSquare, Loader2, Send
} from "lucide-react";
import { Sidebar } from "@/components/layout/Sidebar";

export const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [betaFeedbackOpen, setBetaFeedbackOpen] = useState(false);
  const [betaFeedback, setBetaFeedback] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const handleSubmitBetaFeedback = async () => {
    if (!betaFeedback.trim()) return;
    setSubmittingFeedback(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'beta_feedback',
          message: betaFeedback,
          email: 'beta_tester',
          timestamp: new Date().toISOString()
        })
      });
      toast.success("Thank you for your feedback!");
      setBetaFeedback("");
      setBetaFeedbackOpen(false);
    } catch (err) {
      toast.error("Failed to submit feedback");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-afa-navy flex items-center justify-between px-4 z-40">
        <button onClick={() => setMobileOpen(true)} className="p-2 text-white">
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-afa-lime rounded-lg flex items-center justify-center">
            <Mail className="w-4 h-4 text-afa-navy" />
          </div>
          <span className="font-bold text-white">Simply Smarter Email</span>
        </div>
        <div className="w-10" />
      </header>

      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        {children}
      </main>

      {/* Beta Testing Banner - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-64 z-30">
        <div className="bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-amber-900">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium">
                You are BETA testing this app! See something we should look into?
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setBetaFeedbackOpen(true)}
                className="text-sm font-semibold text-amber-900 hover:text-amber-950 underline underline-offset-2"
              >
                Share Feedback
              </button>
              <span className="text-amber-800 text-sm hidden sm:inline">or email</span>
              <a
                href="mailto:info@anneflinn.com"
                className="text-sm font-semibold text-amber-900 hover:text-amber-950 underline underline-offset-2"
              >
                info@anneflinn.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Beta Feedback Modal */}
      {betaFeedbackOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setBetaFeedbackOpen(false)} />
          <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-xl">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Beta Feedback</h3>
                  <p className="text-sm text-slate-500">Help us improve! Share what you've noticed.</p>
                </div>
              </div>

              <textarea
                value={betaFeedback}
                onChange={(e) => setBetaFeedback(e.target.value)}
                placeholder="What did you notice? Any bugs, suggestions, or things that confused you?"
                className="w-full h-32 p-4 border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-amber-400 text-slate-700"
              />

              <div className="flex items-center justify-between mt-4">
                <p className="text-xs text-slate-400">
                  Or email us directly at <a href="mailto:info@anneflinn.com" className="text-amber-600 underline">info@anneflinn.com</a>
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setBetaFeedbackOpen(false)}
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitBetaFeedback}
                    disabled={submittingFeedback || !betaFeedback.trim()}
                    className="bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                  >
                    {submittingFeedback ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                    Send Feedback
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
